import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import keyBy from 'lodash/keyBy';

export interface Data {
  energyLevel: ProgramGenerator;
  fitnessLevel: ProgramGeneratorFitnessLevel;
  gymEquipments: ProgramGeneratorGymEquipments;
  injuriesLimitations: ProgramGeneratorInjuriesLimitations;
  movementInsights: ProgramGeneratorMovementInsights;
  nonGymActivity: ProgramGeneratorNonGymActivity;
  programDuration: ProgramGeneratorProgramDuration;
  programIntensity: ProgramGenerator;
  rehabilitationStage: ProgramGeneratorRehabilitationStage;
  secondaryTrainingGoals: ProgramGeneratorSecondaryTrainingGoals;
  trainingGoal: ProgramGenerator;
  trainingGoalApproaches: ProgramGeneratorTrainingGoal;
  trainingGoalDetails: ProgramGeneratorTrainingGoal;
  trainingGoalPreferences: ProgramGeneratorTrainingGoal;
  trainingSchedule: ProgramGeneratorTrainingSchedule;
  weightLossTarget: ProgramGeneratorWeightLossTarget;
  workoutDuration: ProgramGeneratorWorkoutDuration;
}

export interface OnboardingClientType {
  options: OnboardingClientTypeOption[];
  subtitle: string;
  title: string;
  type: string;
}

export interface OnboardingClientTypeOption {
  bullets: string[];
  description: string;
  order: number;
  slug: string;
  title: string;
}

export interface ProgramGenerator {
  id: string;
  options: ProgramGeneratorEnergyLevelOption[];
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorEnergyLevelOption {
  id: string;
  image: string;
  order: number;
  slug: string;
  title: string;
}

export interface ProgramGeneratorFitnessLevel {
  id: string;
  options: ProgramGeneratorFitnessLevelOption[];
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorFitnessLevelOption {
  id: string;
  order: number;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorInjuriesLimitations {
  id: string;
  note: Note;
  options: ProgramGeneratorEnergyLevelOption[];
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
  subtitle: string;
  title: string;
}

export interface Note {
  id: string;
  placeholder: string;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorMovementInsights {
  commonOptions: CommonOptionElement[];
  headerTitle: string;
  id: string;
  items: Item[];
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
}

export interface CommonOptionElement {
  id: string;
  order: number;
  slug: string;
  title: string;
}

export interface Item {
  headerTitle: string;
  id: string;
  order: number;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorNonGymActivity {
  id: string;
  options: ProgramGeneratorNonGymActivityOption[];
  order: number;
  required: boolean;
  selectionType: string;
  slider: ProgramGeneratorNonGymActivitySlider;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorNonGymActivityOption {
  id: string;
  image: string;
  maxValue: number;
  minValue: number;
  order: number;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorNonGymActivitySlider {
  max: number;
  min: number;
  step: number;
}

export interface ProgramGeneratorProgramDuration {
  id: string;
  options: CommonOptionElement[];
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorRehabilitationStage {
  goalID: string;
  id: string;
  options: ProgramGeneratorEnergyLevelOption[];
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorSecondaryTrainingGoals {
  id: string;
  maxSelection: number;
  minSelection: number;
  options: ProgramGeneratorEnergyLevelOption[];
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorTrainingGoal {
  groups: Group[];
  id: string;
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
}

export interface Group {
  goalID: string;
  options: ProgramGeneratorEnergyLevelOption[];
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorTrainingSchedule {
  fields: Field[];
  id: string;
  order: number;
  required: boolean;
  slug: string;
  subtitle: string;
  title: string;
}

export interface Field {
  id: string;
  options: FieldOption[];
  required: boolean;
  selectionType: string;
  slug: string;
  title: string;
}

export interface FieldOption {
  id: string;
  order: number;
  shortTitle?: string;
  slug: string;
  title: string;
  image?: string;
}

export interface ProgramGeneratorWeightLossTarget {
  goalID: string;
  id: string;
  order: number;
  required: boolean;
  selectionType: string;
  slider: ProgramGeneratorWeightLossTargetSlider;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorWeightLossTargetSlider {
  max: number;
  min: number;
  step: number;
  supportedUnits: string[];
  unit: string;
}

export interface ProgramGeneratorWorkoutDuration {
  id: string;
  options: ProgramGeneratorWorkoutDurationOption[];
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
  subtitle: string;
  title: string;
}

export interface ProgramGeneratorWorkoutDurationOption {
  durationInMinutes: number;
  id: string;
  order: number;
  slug: string;
  title: string;
}

import { deepCamelCaseKeys } from 'src/utils/deepCamelCaseKeys';

export interface ProgramGeneratorGymEquipments {
  id: string;
  order: number;
  required: boolean;
  selectionType: string;
  slug: string;
  subtitle: string;
  title: string;
  tabs: { id: string; title: string }[];
  options: ProgramGeneratorGymEquipmentOption[];
}

export interface ProgramGeneratorGymEquipmentOption {
  id: string;
  image: string;
  order: number;
  slug: string;
  tab: string;
  title: string;
}

const ONE_WEEK_MS = 1000 * 60 * 60 * 24 * 7; // 7 days (1 week)

export const fetchProgramMasterData = async (): Promise<Data> => {
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_PROGRAM_MASTER_DATA}`;

  const response = await fetch(apiUrl);

  if (!response.ok) {
    throw new Error(`Failed to fetch program master data: ${response.status}`);
  }

  const res = await response.json();

  // Standard industry approach: use lodash keyBy to normalize array to a dictionary using 'id'
  const dataObj = Array.isArray(res.data) ? keyBy(res.data, 'id') : res.data;
  const parsedData = deepCamelCaseKeys(dataObj);

  return parsedData as unknown as Data;
};

export function useGetProgramMasterData() {
  return useQuery({
    queryKey: ['program-master-data'],
    queryFn: fetchProgramMasterData,
    staleTime: ONE_WEEK_MS, // 7 days (1 week)
    gcTime: ONE_WEEK_MS, // 7 days (1 week)
  });
}
